import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Lock, ShieldCheck, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DocumentsTab, TimelineTab } from "./EmployeeDetailPage";

const LOCKED_KEYS = ["employee_code", "email", "department", "designation", "employment_type", "join_date", "status"];

export default function MyProfilePage() {
  const { user } = useAuth();
  const [emp, setEmp] = useState(null);
  const [probation, setProbation] = useState(null);
  const [pending, setPending] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/me/profile");
    setEmp(data.employee);
    setProbation(data.probation || null);
    setPending(data.pending_change);
    const e = data.employee || {};
    setForm({
      phone: e.phone || "",
      personal_email: e.personal_email || "",
      address: e.address || "",
      emergency_contact_name: e.emergency_contact_name || "",
      emergency_contact_phone: e.emergency_contact_phone || "",
      pan: e.pan || "",
      aadhaar: e.aadhaar || "",
      bank_name: e.bank_name || "",
      bank_account: e.bank_account || "",
      bank_ifsc: e.bank_ifsc || "",
      account_holder_name: e.account_holder_name || "",
    });
  };
  useEffect(() => { load(); }, []);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, pan: form.pan ? form.pan.toUpperCase() : null, bank_ifsc: form.bank_ifsc ? form.bank_ifsc.toUpperCase() : null };
      const { data } = await api.patch("/me/profile", payload);
      toast.success(data.pending_change ? "Updated. Sensitive changes are pending admin approval." : "Profile updated");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (!emp) return <div className="text-sm text-zinc-500">Loading…</div>;
  const proposed = pending?.proposed || {};
  const pendingBadge = (key) => proposed[key] != null && (
    <Badge variant="outline" className="ml-2 text-[10px] bg-amber-50 text-amber-700 border-amber-200"><Clock className="h-3 w-3 mr-1" /> Pending review</Badge>
  );

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">My profile</h1>
        <p className="mt-1 text-sm text-zinc-500">Update your personal details. Sensitive fields require admin approval.</p>
      </div>

      <Tabs defaultValue="details" className="space-y-4">
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="docs">Documents</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-6">

      {probation && probation.state !== "unknown" && (
      <Card className="border-zinc-200 shadow-none">
      <CardContent className="p-4">
      <div className="text-xs text-zinc-500">
      Probation · {probation.months} month{probation.months === 1 ? "" : "s"}
      </div>
      <div className="text-sm font-medium text-zinc-900 mt-1" data-testid="my-probation">
      {probation.state === "completed"
      ? `Completed on ${new Date(probation.end).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`
      : probation.state === "ended_early"
      ? `Ended ${new Date(probation.end).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`
      : `Completes on ${new Date(probation.end).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })} · ${probation.days_left} day${probation.days_left === 1 ? "" : "s"} to go`}
      {probation.extended ? " · extended" : ""}
      </div>
      </CardContent>
      </Card>
      )}

      <Card className="border-zinc-200 shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base font-semibold"><Lock className="h-4 w-4 text-zinc-400" /> Work details (read-only)</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs text-zinc-500">Employee code</Label><Input value={emp.employee_code || ""} readOnly className="h-9 bg-zinc-50" /></div>
          <div><Label className="text-xs text-zinc-500">Work email</Label><Input value={emp.email || ""} readOnly className="h-9 bg-zinc-50" /></div>
          <div><Label className="text-xs text-zinc-500">Department</Label><Input value={emp.department || ""} readOnly className="h-9 bg-zinc-50" /></div>
          <div><Label className="text-xs text-zinc-500">Designation</Label><Input value={emp.designation || ""} readOnly className="h-9 bg-zinc-50" /></div>
          <div><Label className="text-xs text-zinc-500">Employment type</Label><Input value={(emp.employment_type || "").replace(/_/g, " ")} readOnly className="h-9 bg-zinc-50 capitalize" /></div>
          <div><Label className="text-xs text-zinc-500">Manager</Label><Input value={emp.manager_name || "—"} readOnly className="h-9 bg-zinc-50" /></div>
              <div><Label className="text-xs text-zinc-500">Join date</Label><Input value={emp.join_date ? dayjs(emp.join_date).format("D MMM YYYY") : "—"} readOnly className="h-9 bg-zinc-50" /></div>
        </CardContent>
      </Card>

      <form onSubmit={submit} className="space-y-5" data-testid="my-profile-form">
        <Card className="border-zinc-200 shadow-none">
          <CardHeader className="pb-2"><CardTitle className="text-base font-semibold">Personal contact</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Mobile number</Label><Input value={form.phone} onChange={change("phone")} className="h-9" /></div>
            <div><Label className="text-xs">Personal email</Label><Input type="email" value={form.personal_email} onChange={change("personal_email")} className="h-9" /></div>
            <div className="col-span-2"><Label className="text-xs">Address</Label><Textarea rows={2} value={form.address} onChange={change("address")} /></div>
            <div><Label className="text-xs">Emergency contact name</Label><Input value={form.emergency_contact_name} onChange={change("emergency_contact_name")} className="h-9" /></div>
            <div><Label className="text-xs">Emergency contact phone</Label><Input value={form.emergency_contact_phone} onChange={change("emergency_contact_phone")} className="h-9" /></div>
          </CardContent>
        </Card>

        <Card className="border-zinc-200 shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base font-semibold"><ShieldCheck className="h-4 w-4 text-emerald-600" /> KYC & Bank details</CardTitle>
            <p className="text-xs text-amber-700">Changes here need admin approval — payroll uses the current approved values until then.</p>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">PAN {pendingBadge("pan")}</Label><Input value={form.pan} onChange={change("pan")} className="h-9 uppercase" placeholder="ABCDE1234F" pattern="[A-Za-z]{5}[0-9]{4}[A-Za-z]" maxLength={10} /></div>
            <div><Label className="text-xs">Aadhaar {pendingBadge("aadhaar")}</Label><Input value={form.aadhaar} onChange={change("aadhaar")} className="h-9" placeholder="12 digits" pattern="\d{12}" maxLength={12} /></div>
            <div><Label className="text-xs">Bank name {pendingBadge("bank_name")}</Label><Input value={form.bank_name} onChange={change("bank_name")} className="h-9" /></div>
            <div><Label className="text-xs">IFSC {pendingBadge("bank_ifsc")}</Label><Input value={form.bank_ifsc} onChange={change("bank_ifsc")} className="h-9 uppercase" pattern="[A-Za-z]{4}0[A-Za-z0-9]{6}" maxLength={11} /></div>
            <div><Label className="text-xs">Bank account {pendingBadge("bank_account")}</Label><Input value={form.bank_account} onChange={change("bank_account")} className="h-9" pattern="\d{9,18}" maxLength={18} /></div>
            <div><Label className="text-xs">Account holder {pendingBadge("account_holder_name")}</Label><Input value={form.account_holder_name} onChange={change("account_holder_name")} className="h-9" /></div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 h-10" data-testid="my-profile-save">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </form>
        </TabsContent>

        <TabsContent value="docs">
          <DocumentsTab employeeId={emp.id} isAdmin={false} canUpload />
        </TabsContent>

        <TabsContent value="timeline">
          <TimelineTab employeeId={emp.id} isAdmin={false} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
